import { buildUri, getBaseUrl, handleError, handleResponse } from "./apiUtils";

import { toast } from "react-toastify";

const baseUrl = getBaseUrl();

const downloadCompleteMessage =
  "Download complete, please check your downloads folder";

export async function getInvoiceCount({
  searchKeys,
  queryString,
  includeInactive = false,
  complexName,
  statusFilters,
  startDate,
  endDate,
}) {
  let fetchInvoiceCountUri = `${baseUrl}/invoice/getInvoiceCount/`;

  if (searchKeys || queryString) {
    fetchInvoiceCountUri = buildUri(
      fetchInvoiceCountUri,
      searchKeys,
      queryString,
    );
    fetchInvoiceCountUri = fetchInvoiceCountUri.concat(
      `includeInactive=${includeInactive}`,
    );
  } else {
    fetchInvoiceCountUri = fetchInvoiceCountUri.concat(
      `?includeInactive=${includeInactive}`,
    );
  }

  if (complexName) {
    fetchInvoiceCountUri = fetchInvoiceCountUri.concat(
      `&complexName=${complexName}`,
    );
  }

  if (startDate) {
    fetchInvoiceCountUri = fetchInvoiceCountUri.concat(
      `&startDate=${startDate}`,
    );
  }
  if (endDate) {
    fetchInvoiceCountUri = fetchInvoiceCountUri.concat(`&endDate=${endDate}`);
  }

  if (statusFilters && statusFilters.length > 0) {
    fetchInvoiceCountUri = fetchInvoiceCountUri.concat(
      `&statusFilters=${statusFilters}`,
    );
  }

  return await fetch(fetchInvoiceCountUri)
    .then(handleResponse)
    .catch(handleError);
}

export async function getInvoices({
  searchKeys,
  queryString,
  orderKey,
  isAscending,
  skip,
  take,
  complexName,
  statusFilters,
  startDate,
  endDate,
  includeInactive = false,
}) {
  let fetchInvoicesUri = `${baseUrl}/invoice/getInvoices/`;

  if (searchKeys || queryString || orderKey || isAscending || skip || take) {
    fetchInvoicesUri = buildUri(
      fetchInvoicesUri,
      searchKeys,
      queryString,
      orderKey,
      isAscending,
      skip,
      take,
    );

    fetchInvoicesUri = fetchInvoicesUri.concat(
      `includeInactive=${includeInactive}`,
    );
  } else {
    fetchInvoicesUri = fetchInvoicesUri.concat(
      `?includeInactive=${includeInactive}`,
    );
  }

  if (complexName) {
    fetchInvoicesUri = fetchInvoicesUri.concat(`&complexName=${complexName}`);
  }

  if (startDate) {
    fetchInvoicesUri = fetchInvoicesUri.concat(`&startDate=${startDate}`);
  }

  if (endDate) {
    fetchInvoicesUri = fetchInvoicesUri.concat(`&endDate=${endDate}`);
  }

  if (statusFilters && statusFilters.length > 0) {
    fetchInvoicesUri = fetchInvoicesUri.concat(
      `&statusFilters=${statusFilters}`,
    );
  }

  return await fetch(fetchInvoicesUri).then(handleResponse).catch(handleError);
}

export async function downloadCombinedInvoices({ invoiceNumbers }) {
  let downloadInvoicesUri = `${baseUrl}/invoice/getInvoicePdfs`;

  return await fetch(downloadInvoicesUri, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      invoiceNumbers,
      combineFiles: true,
    }),
  })
    .then(async (response) => {
      if (response.status !== 200) {
        toast.error(
          "There was a problem downloading the invoices try again after 1 minute.",
        );
      } else {
        var jsonResponse = await response.json();

        if (jsonResponse.message !== null) {
          toast.warning(jsonResponse.message, { autoClose: false });
        }

        const linkSource = `data:application/pdf;base64,${jsonResponse.payload}`;
        const downloadLink = document.createElement("a");
        const invoiceName = jsonResponse.invoiceNumbers.length > 3
          ? `${jsonResponse.invoiceNumbers.slice(0, 3).join("_")}_and_${jsonResponse.invoiceNumbers.length - 3}_more`
          : jsonResponse.invoiceNumbers.join("_");
        const fileName = `Inv_${invoiceName}.pdf`;
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
        downloadLink.remove();
        toast.success(downloadCompleteMessage);
      }
    })
    .catch(handleError);
}

export async function downloadZippedInvoices({ invoiceNumbers }) {
  let downloadInvoicesUri = `${baseUrl}/invoice/getInvoicePdfs`;

  return await fetch(downloadInvoicesUri, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      invoiceNumbers,
      combineFiles: false,
    }),
  })
    .then(async (response) => {
      if (response.status !== 200) {
        toast.error(
          "There was a problem downloading the invoices try again after 1 minute.",
        );
      }
      var jsonResponse = await response.json();

      if (jsonResponse.message !== null) {
        toast.warning(jsonResponse.message, { autoClose: false });
      }

      const linkSource = `data:application/zip;base64,${jsonResponse.payload}`;
      const downloadLink = document.createElement("a");
      const invoiceName = jsonResponse.invoiceNumbers.length > 3
        ? `${jsonResponse.invoiceNumbers.slice(0, 3).join("_")}_and_${jsonResponse.invoiceNumbers.length - 3}_more`
        : jsonResponse.invoiceNumbers.join("_");
      const fileName = `Inv_${invoiceName}.zip`;
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
      downloadLink.remove();
      toast.success(downloadCompleteMessage);
    })
    .catch(handleError);
}

export async function sendInvoices({ invoiceNumbers }) {
  let sendInvoiceUri = `${baseUrl}/invoice/sendInvoices`;

  return await fetch(sendInvoiceUri, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ invoiceNumbers }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function deleteInvoice(invoiceNumberToDelete) {
  let deleteInvoiceUri = `${baseUrl}/invoice/deleteInvoice/${invoiceNumberToDelete}`;

  return await fetch(deleteInvoiceUri, {
    method: "DELETE",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function createPrimarySecondaryCharges(primarySecondaryCharges) {
  let sendInvoiceUri = `${baseUrl}/invoice/createPrimarySecondaryCharges`;

  return await fetch(sendInvoiceUri, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(primarySecondaryCharges),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function createCharge(chargeToCreate) {
  let sendInvoiceUri = `${baseUrl}/invoice/createcharge`;

  return await fetch(sendInvoiceUri, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(chargeToCreate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function createCharges(chargesToCreate) {
  let sendInvoiceUri = `${baseUrl}/invoice/createCharges`;

  return await fetch(sendInvoiceUri, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(chargesToCreate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getReadyForInvoiceCharges(division, zeroDollarMode) {
  let getChargesUri = `${baseUrl}/invoice/getReadyForInvoiceCharges`;

  if (division) {
    getChargesUri += `?division=${division}`;
  }
  if (zeroDollarMode) {
    getChargesUri += `?zeroDollarMode=${zeroDollarMode}`;
  }

  return await fetch(getChargesUri, {
    method: "GET",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getChargesByInvoiceNumber(invoiceNumber) {
  const requestUrl = `${baseUrl}/invoice/getChargesByInvoiceNumber/${invoiceNumber}`;

  return await fetch(requestUrl, {
    method: "GET",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getReadyForInvoiceChargeByOrderedJobId(orderedJobId) {
  const requestUrl = `${baseUrl}/invoice/getReadyForInvoiceChargeByOrderedJobId/${orderedJobId}`;

  return await fetch(requestUrl, {
    method: "GET",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getInvoicedChargeByOrderedJobId(orderedJobId) {
  const requestUrl = `${baseUrl}/invoice/getInvoicedChargeByOrderedJobId/${orderedJobId}`;

  return await fetch(requestUrl, {
    method: "GET",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function checkFuturePurchaseOrder(invoicesForCreate) {
  const requestUrl = `${baseUrl}/invoice/hasFuturePos`;

  return await fetch(requestUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(invoicesForCreate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function createInvoices(invoicesForCreate) {
  const requestUrl = `${baseUrl}/invoice/createInvoices`;

  return await fetch(requestUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(invoicesForCreate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function updateCharge(chargeForUpdate) {
  const requestUrl = `${baseUrl}/invoice/updatecharge/${chargeForUpdate.id}`;
  delete chargeForUpdate.primaryCharge;
  delete chargeForUpdate.complex;
  return await fetch(requestUrl, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(chargeForUpdate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function deleteCharges(chargeIds, keepTracking) {
  if (keepTracking !== true) {
    keepTracking = false;
  }
  const requestUrl = `${baseUrl}/invoice/deletecharges?keepTracking=${keepTracking}`;

  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(chargeIds),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function addChargeToInvoice(invoiceNumber, chargeId) {
  const requestUrl = `${baseUrl}/invoice/addchargetoinvoice/${invoiceNumber}/${chargeId}`;

  return await fetch(requestUrl, {
    method: "POST",
  });
}

export async function removeChargeFromInvoice(
  invoiceNumber,
  chargeId,
  convertToPrimaryCharge = false,
) {
  const requestUrl = `${baseUrl}/invoice/removeChargeFromInvoice/${invoiceNumber}/${chargeId}?convertToPrimaryCharge=${convertToPrimaryCharge}`;

  return await fetch(requestUrl, {
    method: "POST",
  });
}

export async function updateChargeInInvoice(invoiceNumber, chargeForUpdate) {
  const requestUrl = `${baseUrl}/invoice/updatechargeininvoice/${invoiceNumber}/${chargeForUpdate.id}`;
  delete chargeForUpdate.primaryCharge;
  delete chargeForUpdate.complex;

  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(chargeForUpdate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function createContract(contractForCreate) {
  const requestUrl = `${baseUrl}/invoice/createContract`;
  delete contractForCreate.id;

  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(contractForCreate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function processContracts(monthToProcess) {
  const requestUrl = `${baseUrl}/invoice/processContracts?monthToProcess=${monthToProcess}`;

  return await fetch(requestUrl, {
    method: "GET",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function forceUpdateInvoicePaymentStatusFromQBO() {
  const requestUrl = `${baseUrl}/invoice/forceUpdateInvoicePaymentStatusFromQBO`;

  return await fetch(requestUrl, {
    method: "GET",
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function updateContract(contractForUpdate) {
  const requestUrl = `${baseUrl}/invoice/updateContract`;

  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(contractForUpdate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getComplexesWithContracts(startDate, endDate) {
  const requestUrl = `${baseUrl}/invoice/GetComplexesWithContractsInRange?startDate=${startDate}&endDate=${endDate}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function addDailyScheduleEntry(scheduleId, entry) {
  const requestUrl = `${baseUrl}/schedule/CreateNewDailyScheduleEntry/${scheduleId}`;
  return await fetch(requestUrl, {
    method: 'POST',
    headers: { "content-type": "application/json" },
    body: JSON.stringify(entry),
  })
  .then(handleResponse)
  .catch(handleError)
}

export async function deleteDailyScheduleEntry(entryId) {
  const requestUrl = `${baseUrl}/schedule/DeleteDailyScheduleEntry/${entryId}`;
  return await fetch(requestUrl, {method: 'DELETE'}).then(handleResponse).catch(handleError);
}

export async function getContractsActiveOnDate(date) {
  const requestUrl = `${baseUrl}/invoice/GetContractsActiveOnDate?date=${date}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getContracts(complexId, includeInactive, includePending) {
  let requestUrl = `${baseUrl}/invoice/getContracts/?`;

  if (complexId) {
    requestUrl = requestUrl + `${complexId}`;
  }

  if (includeInactive) {
    requestUrl = requestUrl + `&includeInactive=true`;
  } else {
    requestUrl = requestUrl + `&includeInactive=false`;
  }

  if (includePending) {
    requestUrl = requestUrl + `&includePending=true`;
  } else {
    requestUrl = requestUrl + `&includePending=false`;
  }

  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getContract(contractId) {
  let requestUrl = `${baseUrl}/invoice/getContract/${contractId}`;

  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getContractTemplateSchedule(contractId) {
  let requestUrl = `${baseUrl}/schedule/GetContractTemplateSchedule/${contractId}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function updateContractTemplateSchedule(templateForUpdate) {
  let requestUrl = `${baseUrl}/schedule/UpdateContractTemplateSchedule/`;
  return await fetch(requestUrl, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(templateForUpdate),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getDailySchedule(date) {
  let requestUrl = `${baseUrl}/schedule/GetDailySchedule/${date.toDateString()}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function resetDailySchedule(date) {
    let requestUrl = `${baseUrl}/schedule/ResetDailySchedule/${date.toDateString()}`;
    return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function checkIfDailyScheduleSet(date) {
  let requestUrl = `${baseUrl}/schedule/CheckIfDailyScheduleSet/${date.toDateString()}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function updateDailyScheduleEntry(
  id,
  employeeId,
  startTime,
  endTime,
) {
  let requestUrl = `${baseUrl}/schedule/updateDailyScheduleEntry/`;
  return await fetch(requestUrl, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      id,
      actualEmployeeId: employeeId,
      startTime,
      endTime,
    }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function approveDailySchedule(dailyScheduleId) {
  let requestUrl = `${baseUrl}/schedule/approveDailySchedule/${dailyScheduleId}`;
  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function unapproveDailySchedule(dailyScheduleId) {
  let requestUrl = `${baseUrl}/schedule/UnapproveDailySchedule/${dailyScheduleId}`;
  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function getVarianceReport(date) {
  let requestUrl = `${baseUrl}/schedule/getVarianceReport/${date.toDateString()}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function updateVarianceReportEntryNote(entryId, note) {
  let requestUrl = `${baseUrl}/schedule/updateVarianceReportNote/${entryId}`;
  return await fetch(requestUrl, {
    method: "PUT",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ notes: note, id: entryId }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function approveVarianceReport(reportId) {
  let requestUrl = `${baseUrl}/schedule/approveVarianceReport/${reportId}`;
  return await fetch(requestUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
  })
    .then(handleResponse)
    .catch(handleError);
}

export async function resetVarianceReport(date) {
    let requestUrl = `${baseUrl}/schedule/ResetVarianceReport/${date.toDateString()}`;
    return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getContractSchedules(startMonth) {
  const requestUrl = `${baseUrl}/schedule/GetContractScheduleForMonth?startMonth=${startMonth}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getContractWeeklyFloaterInfo(startDate, endDate) {
  const requestUrl = `${baseUrl}/schedule/GetFloaterWeeklyInformation?startDate=${startDate}&endDate=${endDate}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function exportContractSchedules(startMonth, endMonth){
  const requestUrl = `${baseUrl}/schedule/ExportContractSchedule?startMonth=${startMonth}&endMonth=${endMonth}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function importSchedules(formData, devMode = false) {
  const requestUrl = `${baseUrl}/schedule/ImportSchedules?devMode=${devMode}`;

  return await fetch(requestUrl, {
    method: "POST",
    body: formData})
    .then(async (response) => {
      if (response.status !== 200) {
        toast.error(
          "There was a problem importing schedules. Please verify the document for import is formatted correctly."
        );
      }
      const jsonResponse = await response.json();
      if (jsonResponse.succeeded) {
        let textResponse = "";

        if (jsonResponse.addedItems.length > 0) {
          textResponse = textResponse.concat(
            `The following items were added:\n\n`
          );

          jsonResponse.addedItems.forEach((item) => {
            textResponse = textResponse.concat(
              `${item}\n`
            );
          });
        }

        if (jsonResponse.modifiedContracts.length > 0) {
          textResponse = textResponse.concat(
            `The following contracts were modified:\n\n`
          );

          jsonResponse.modifiedContracts.forEach((contract) => {
            textResponse = textResponse.concat(
              `${contract}\n`
            );
          });
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        var link = document.createElement("a");
        link.download = `Additions/Modifications_${
          today.getMonth() + 1
        }_${today.getDate()}_${today.getFullYear()}.txt`;
        var blob = new Blob([textResponse], { type: "text/plain" });
        link.href = window.URL.createObjectURL(blob);
        link.click();
        link.remove();
        toast.success("Import Successful: Please see the attached file for details");
      } else {
        let textResponse = "";
        
        if (jsonResponse.readerResult.errorRows.length > 0) {
            textResponse = textResponse.concat(
              `There were errors with the provided import file. Please see below for details.\n\n`
            );

          jsonResponse.readerResult.errorRows.forEach((rowWithError) => {
            textResponse = textResponse.concat(
              `Row: ${rowWithError.rowIndex} ${rowWithError.errorMessage}\n`
            );
          });
        }

        if (jsonResponse.errors.length > 0) {
          textResponse = textResponse.concat(
            `There were errors that occurred during processing of updates from the import file:\n\n`
          );

          jsonResponse.errors.forEach((error) => {
            textResponse = textResponse.concat(
              `${error}\n`
            );
          });
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        var link = document.createElement("a");
        link.download = `Errors_${
          today.getMonth() + 1
        }_${today.getDate()}_${today.getFullYear()}.txt`;
        var blob = new Blob([textResponse], { type: "text/plain" });
        link.href = window.URL.createObjectURL(blob);
        link.click();
        link.remove();
        toast.error("Import Failed: Please see the attached file");
      };
    })
}

export async function getEmployeeContractComplexes(employeeId) {
  const requestUrl = `${baseUrl}/schedule/GetEmployeeContractComplexes/${employeeId}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getMonthlyVarianceReport(monthStart) {
  const requestUrl = `${baseUrl}/schedule/GetMonthlyVarianceReport?monthStart=${monthStart}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}

export async function getMonthlyVarianceReportEntriesForComplex(request) {
  const requestUrl = `${baseUrl}/schedule/GetMonthlyVarianceEntriesForComplex`;
  return await fetch(requestUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  })
  .then(handleResponse)
  .catch(handleError)
}

export async function exportMonthlyVarianceReport(monthStart) {
  const requestUrl = `${baseUrl}/schedule/ExportMonthlyVarianceReport?monthStart=${monthStart}`;
  return await fetch(requestUrl).then(handleResponse).catch(handleError);
}